(function (global, factory) {
  'use strict';

  if (typeof define === 'function' && define.amd) {
    // If supports for AMD is available return the value to the AMD Loader
    // and to the global (window) object in case any JS module.
    define('formValidator',[], function () {
      return (global.formValidator = factory(global));
    });
  } else if (typeof exports === 'object') {
    // If supports for Common JS is available export the module
    // and pass is value to the global (window) object in case non Common JS code
    // is expected to work
    module.exports = (global.formValidator = factory(global));
  } else {
    // If not support for AMD or Common JS is available register the module in the global scope.
    global.formValidator = factory(global);
  }
}(typeof window !== 'undefined' ? window : {}, function () {
  'use strict';

  /**
  * Validates the email address by sending a request to the server.
  * 
  * This function takes a payload containing the email address, sends a 
  * POST request to the validation endpoint, and returns the validation 
  * result. If the response is not ok, it assumes the email is valid.
  * 
  * @param {Object} payload - The payload containing the email address to validate.
  * @param {string} payload.emailAddress - The email address to be validated.
  * 
  * @returns {Promise<boolean>} - A promise that resolves to a boolean indicating 
  * whether the email address is valid or not.
  */
  async function validateEmailAddress(payload) {
    try {
      const response = await fetch(`${window.location.origin}/ajax/formvalidation/validateemailaddress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return false;
    }
  }

  /**
    * Retrieves the error element associated with the given input element.
    * 
    * This function searches for an error message element within the input's 
    * parent and grandparent elements. It looks for elements with the 
    * attribute `data-error-message` or a class of `ao-form-error-message`.
    * 
    * @param {HTMLInputElement} input - The input element for which to find the 
    * associated error message element.
    * 
    * @returns {HTMLElement|null} - The error message element if found, 
    * otherwise null.
    */
  function getErrorElement(input) {
    return input.parentElement.querySelector('[data-error-message]') ||
      input.parentElement.parentElement.querySelector('[data-error-message]') ||
      input.parentElement.querySelector('.ao-form-error-message');
  }

  /**
    * Adds error styles and messages to the input element.
    * 
    * This function applies visual error styles to the input element when 
    * validation fails. It sets the appropriate ARIA attributes for accessibility 
    * and updates the error message based on whether the input is required 
    * and its current value.
    * 
    * @param {HTMLInputElement} input - The input element to which error styles 
    * will be added.
    * 
    * If the input is required and empty, a required error message is displayed. 
    * If the input is not valid, an invalid error message is shown.
    */
  function showInputError(input) {
    input.setAttribute('aria-invalid', true);
    input.classList.add('nhs_Error');

    const isRequiredError = input.required && input.value === '';
    const errorElement = getErrorElement(input);

    if (errorElement) {
      const errorMessage = isRequiredError ?
        errorElement.getAttribute('data-required-message') :
        errorElement.getAttribute('data-invalid-message');

      errorElement.textContent = errorMessage;
      input.setCustomValidity(errorMessage);
      input.setAttribute('aria-describedby', errorElement.id);
    }
  }

  /**
    * Removes error styles and messages from the input element.
    * 
    * This function resets the input element's error state by removing
    * any error-related attributes and classes. It also clears the error
    * message displayed in the associated error element.
    * 
    * @param {HTMLInputElement} input - The input element to remove error styles from.
    */
  function clearInputError(input) {
    input.removeAttribute('aria-invalid');
    input.setCustomValidity('');
    input.removeAttribute('aria-describedby');
    input.classList.remove('nhs_Error');

    const errorElement = getErrorElement(input);
    if (errorElement) {
      errorElement.textContent = '';
    }
  }

  /**
  * Handles the result of the email validation by updating the email input field
  * based on whether the email address is valid or not.
  * 
  * @param {HTMLInputElement} emailInput - The input element containing the email address.
  * @param {boolean} isValid - A boolean indicating whether the email address is valid.
  * 
  * If the email address is valid, a data attribute is added to the input element to
  * indicate this. If the email address is invalid, the data attribute is removed.
  * The input element is also enabled after the validation result is processed.
  */
  function handleEmailValidationResult(emailInput, isValid) {
    if (isValid) {
      emailInput.setAttribute('data-valid-email-domain', '');
      clearInputError(emailInput);
    } else {
      emailInput.removeAttribute('data-valid-email-domain');
      showInputError(emailInput);
    }
  }

  /**
  * Validates the email input field by checking its value and 
  * sending a request to validate the email address format.
  * 
  * @param {HTMLInputElement} emailInput - The input element 
  * containing the email address to be validated.
  * 
  * If the email input is empty, it removes the valid email 
  * domain attribute. If the email is not empty, it sends a 
  * request to validate the email address and updates the 
  * input field based on the validation result.
  */
  async function validateEmailDomain(emailInput) {
    if (emailInput && emailInput.value === '') {
      emailInput.removeAttribute('data-valid-email-domain');
      clearInputError(emailInput);
      return false;
    }

    try {
      const isValid = await validateEmailAddress({ emailAddress: emailInput.value });
      handleEmailValidationResult(emailInput, isValid);
      return isValid;
    } catch (error) {
      handleEmailValidationResult(emailInput, true);
      return true;
    }
  }

  let inputsSelector = "fieldset input:enabled:not([type='checkbox']):not([type='button']):not([type='submit']):not([type='hidden']), fieldset textarea, fieldset select:not([disabled])";

  /**
  * Toggles the error message display for the given input element based on its validity.
  * 
  * This function checks if the input is valid or not. If the input is valid, it removes
  * any error styles and messages. If the input is invalid, it adds error styles and 
  * displays the appropriate error message.
  * 
  * @param {HTMLInputElement} input - The input element to toggle the error message for.
  * @param {boolean} isValid - A boolean indicating whether the input is valid or not.
  */
  function toggleErrorMessage(input, isValid) {
    if (isValid) {
      clearInputError(input);
    } else {
      showInputError(input);
    }
  }

  let validateInput = function (inputField) {
    let input = inputField;
    input.value = input.value.trim();
    let isValidInput = true;
    let pattern = new RegExp(input.getAttribute('data-pattern'));

    return (!input.required &&
      (input.value === '' || input.getAttribute('data-pattern') === null || pattern.test(input.value)) ||
      (input.required && pattern.test(input.value)) ||
      (input.required && input.getAttribute('data-pattern') == null && input.value.trim().length > 0));
  };

  let displayFormErrors = function () {
    let errorsList = document.querySelectorAll('[data-error-message]');

    for (let i = 0; i < errorsList.length; i += 1) {
      errorsList[i].style.display = 'block';
    }
  };

  let clearFormErrors = function (form) {
    let errorElements = form.querySelectorAll('[data-error-message]');
    let invalidInputs = form.querySelectorAll("[aria-invalid='true'][aria-describedby]");

    for (let i = 0; i < invalidInputs.length; i += 1) {
      invalidInputs[i].removeAttribute('aria-invalid');
      invalidInputs[i].removeAttribute('aria-describedby');
      invalidInputs[i].classList.remove('nhs_Error');
    }

    for (let i = 0; i < errorElements.length; i += 1) {
      errorElements[i].textContent = '';
    }
  };

  let clearForm = function (form) {
    let inputsList = form.querySelectorAll(inputsSelector);
    for (let i = 0; i < inputsList.length; i += 1) {
      inputsList[i].value = '';
    }

    clearFormErrors(form);
  };

  function addErrorClassToEmailSuggestions(form){
    const suggestionsContainer = form.parentElement.querySelector('[data-suggestions-container]');

    if(suggestionsContainer){
      suggestionsContainer.classList.add('suggestion_error');
    }
  }

  let validateForm = async function (form, avoidFocusFirstInput) {
    let inputsList = form.querySelectorAll(inputsSelector);
    let isValidForm = true;

    clearFormErrors(form);

    for (let i = 0; i < inputsList.length; i += 1) {
      let currentInput = inputsList[i];
      let isValidInput;
      const inputName = currentInput.name.toLowerCase();

      // recommended name attributes by google
      // https://developers.google.com/web/updates/2015/06/checkout-faster-with-autofill
      switch (inputName) {
        case 'fname':
        case 'lname':
        case 'firstname':
        case 'lastname':
        case 'first': // Required by external code used to handle basic leads
        case 'last': // Required by external code used to handle basic leads
        case 'name':
        case 'postal':
        case 'zipcode':
        case 'password':
        case 'passwordconfirm':
        case 'businessphone':
        case 'phone':
        case 'userphonenumber':
        case 'realtorquestion':
        case 'textcomment':
        case 'preferredarrivaltime':
        case 'askstatelist':
        case 'askcommunitylist':
          isValidInput = validateInput(currentInput);
          isValidForm = isValidForm && isValidInput;
          toggleErrorMessage(currentInput, isValidInput);
          break;
        case 'email':
        case 'mailaddress':
          isValidInput = validateInput(currentInput);

          if (isValidInput) {
            isValidInput = await validateEmailDomain(currentInput);
          }

          isValidForm = isValidForm && isValidInput;
          toggleErrorMessage(currentInput, isValidInput);
          addErrorClassToEmailSuggestions(form);
        default:
          break;
      }
    }

    if (!isValidForm && avoidFocusFirstInput !== true) {
      let firstInvalidElement = form.querySelector("input[aria-invalid='true']");
      if (firstInvalidElement) {
        firstInvalidElement.focus();
      }
    }

    displayFormErrors(form);

    return isValidForm;
  };

  return {
    validateForm: validateForm,
    clearForm: clearForm,
    clearFormErrors: clearFormErrors,
    validateInput: validateInput,
  };
}));

